import { Link, useRouter } from 'expo-router';
import { useQuery, useSubscription } from 'urql';
import * as Placeholder from '@/components/Custom/Placeholder';
import EventList from '@/components/Event/EventList';
import {
  EVENT_LIMIT,
  isNextUpcomingEventListItem,
} from '@/components/Event/EventList.utils';
import EventListItem from '@/components/Event/EventListItem';
import { EventListItemInfoFragment } from '@/components/Event/EventListItemInfo';
import {
  EventListItemTeamsFragment,
  EventListItemTeamsRsvpFragment,
} from '@/components/Event/EventListItemTeams';
import { EventListItemTimeFragment } from '@/components/Event/EventListItemTime';
import { EventRsvpPickerFragment } from '@/components/Event/EventRsvpPicker.types';
import * as EventListActions from '@/components/Event/EventScheduleActionButtons';
import ActionBar from '@/components/UI/ActionBar';
import Box from '@/components/UI/Box';
import { graphql } from '@/gql/graphql';
import useActiveTimestamp from '@/hooks/useActiveTimestamp';
import useEventListSession from '@/hooks/useEventListSession';
import useUserId from '@/hooks/useUserId';
import useVisibleListItems from '@/hooks/useVisibleListItems';
import useEventStore from '@/stores/useEventStore';
import { getCalendarDayFromIsoDate } from '@/utils/getCalendarDateAnchors';

const GetEventDetailEvents = graphql(
  /* GraphQL */ `
    query GetEventDetailEvents($eventId: uuid!) {
      event_by_pk(id: $eventId) {
        id
        title
        permissions
      }
    }
  `,
);

const GetEventDetailEventsListFuture = graphql(/* GraphQL */ `
  query GetEventDetailEventsListFuture(
    $where: event_bool_exp!
    $personId: String!
    $limit: Int!
  ) {
    event(
      order_by: [
        { datetime: asc }
        { title: asc }
        { location: asc }
        { id: desc }
      ]
      where: $where
      limit: $limit
    ) @_cursorPagination(cursorArgument: "datetime") {
      id
      datetime
      ...EventListItemTime
      ...EventListItemTeams
      ...EventListItemTeamsRsvp
      ...EventListItemInfo
      ...EventRsvpPicker
    }
  }
`, [
  EventListItemInfoFragment,
  EventListItemTeamsFragment,
  EventListItemTeamsRsvpFragment,
  EventListItemTimeFragment,
  EventRsvpPickerFragment,
]);

const GetEventDetailEventsListPast = graphql(/* GraphQL */ `
  query GetEventDetailEventsListPast(
    $where: event_bool_exp!
    $personId: String!
    $limit: Int!
  ) {
    event(
      order_by: [
        { datetime: desc }
        { title: desc }
        { location: desc }
        { id: asc }
      ]
      where: $where
      limit: $limit
    ) @_cursorPagination(cursorArgument: "datetime") {
      id
      datetime
      ...EventListItemTime
      ...EventListItemTeams
      ...EventListItemTeamsRsvp
      ...EventListItemInfo
      ...EventRsvpPicker
    }
  }
`, [
  EventListItemInfoFragment,
  EventListItemTeamsFragment,
  EventListItemTeamsRsvpFragment,
  EventListItemTimeFragment,
  EventRsvpPickerFragment,
]);

const WatchEventDetailEventsList = graphql(/* GraphQL */ `
  subscription WatchEventDetailEventsList(
    $personId: String!
    $cursor: timestamptz!
    $eventIds: [uuid!]!
  ) {
    event_stream(
      batch_size: 25
      cursor: { initial_value: { updated_at: $cursor } }
      where: { id: { _in: $eventIds } }
    ) {
      ...EventListItemTime
      ...EventListItemTeams
      ...EventListItemTeamsRsvp
      ...EventListItemInfo
      ...EventRsvpPicker
    }
  }
`, [
  EventListItemTimeFragment,
  EventListItemTeamsFragment,
  EventListItemTeamsRsvpFragment,
  EventListItemInfoFragment,
  EventRsvpPickerFragment,
]);

type EventDetailEventsProps = {
  basePath: string;
  eventId: string;
};

function EventDetailEvents({ basePath, eventId }: EventDetailEventsProps) {
  const personId = useUserId();
  const { push } = useRouter();

  const showMyTeams = useEventStore(
    (state) => state.detailFilters[eventId]?.showMyTeams,
  );

  const baseWhere = {
    ...(showMyTeams
      ? {
          roles_by_participant: {
            _has_key: personId,
          },
        }
      : {}),
    parent_id: {
      _eq: eventId,
    },
  };

  const { getVisibleItems, visibleItemIds, onViewableItemsChanged } =
    useVisibleListItems<{
      id: string;
      datetime?: string | null;
    }>();

  const [{ data: eventData }] = useQuery({
    query: GetEventDetailEvents,
    variables: {
      eventId,
    },
  });

  const eventListSession = useEventListSession({
    pause: !personId,
    baseWhere,
    directionLimit: EVENT_LIMIT,
    fieldName: 'event',
    futureQuery: GetEventDetailEventsListFuture,
    pastQuery: GetEventDetailEventsListPast,
    selectFutureItems: (data) => data?.event ?? [],
    selectPastItems: (data) => data?.event ?? [],
    buildVariables: ({ where, limit }) => ({
      where,
      personId: personId ?? '',
      limit,
    }),
    resetScope: {
      kind: 'detail',
      eventId,
    },
  });

  const cursor = useActiveTimestamp();

  useSubscription({
    query: WatchEventDetailEventsList,
    variables: {
      cursor: cursor!,
      eventIds: visibleItemIds,
      personId,
    },
    pause: !cursor || eventListSession.fetching || !visibleItemIds.length,
  });

  const event = eventData?.event_by_pk;

  return (
    <Box flex>
      <ActionBar>
        <EventListActions.Filter
          onPress={() => {
            const firstVisibleItemDate = getVisibleItems()[0]?.datetime;

            push({
              pathname: `/event/[eventId]/events/action`,
              params: {
                eventId,
                date: getCalendarDayFromIsoDate(
                  firstVisibleItemDate ||
                    eventListSession.anchorDate ||
                    undefined,
                ),
              },
            });
          }}
          showMyTeams={showMyTeams}
        />

        <Link
          href={{
            pathname: `/event/[eventId]/events/share`,
            params: { eventId },
          }}
          asChild
        >
          <EventListActions.Share />
        </Link>
        <EventListActions.Refresh onPress={eventListSession.refresh} />

        {event?.permissions?.create_event ? (
          <EventListActions.Create
            onPress={() => {
              push({
                pathname: `/event/create`,
                params: { parentId: eventId, eventType: 'game' },
              });
            }}
          />
        ) : null}
        {event?.permissions?.create_event ? (
          <EventListActions.Generate
            onPress={() => {
              push({
                pathname: '/event/[eventId]/generate',
                params: { eventId },
              });
            }}
          />
        ) : null}
      </ActionBar>

      <Box flex>
        <EventList
          key={eventListSession.listKey}
          initialDate={eventListSession.anchorDate}
          data={eventListSession.data}
          fetching={eventListSession.fetching}
          onPressScrollButton={eventListSession.refresh}
          divideUpcoming
          maintainVisibleContentPosition
          onEndReached={eventListSession.fetchNextPage}
          onStartReached={eventListSession.fetchPreviousPage}
          onViewableItemsChanged={onViewableItemsChanged}
          viewabilityConfig={{
            itemVisiblePercentThreshold: 80,
          }}
          renderItem={({ item, data, index, extraData }) => {
            const nowMs =
              typeof extraData === 'number' ? extraData * 60000 : undefined;

            return (
              <EventListItem
                fragment={item}
                nowMs={nowMs}
                isNextUpcomingEvent={isNextUpcomingEventListItem({
                  data,
                  index,
                  nowMs,
                })}
                onPress={() =>
                  push({
                    pathname: `${basePath}/event/[eventId]`,
                    params: { eventId: item.id },
                  })
                }
              />
            );
          }}
          ListEmptyComponent={
            <Placeholder.Container>
              <Placeholder.Icon name="calendar-outline" />
              <Placeholder.Text>
                {event?.title} has no events scheduled yet.
              </Placeholder.Text>
            </Placeholder.Container>
          }
        />
      </Box>
    </Box>
  );
}

export default EventDetailEvents;
